import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Heart, 
  Shield, 
  Star, 
  Users, 
  Award, 
  Clock, 
  Globe, 
  Sparkles,
  ChevronRight,
  Phone,
  Mail
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion du Beau',
      description: 'Nous sélectionnons chaque villa avec le même soin qu\'un collectionneur choisit une œuvre d\'art.'
    },
    {
      icon: Shield,
      title: 'Excellence du Service',
      description: 'Notre concierge 24/7 anticipe vos besoins pour transformer chaque séjour en expérience inoubliable.'
    },
    {
      icon: Star,
      title: 'Authenticité',
      description: 'Chaque destination révèle ses secrets les mieux gardés grâce à notre réseau local privilégié.'
    },
    {
      icon: Sparkles,
      title: 'Innovation Émotionnelle',
      description: 'Notre approche unique par les émotions vous guide vers la villa qui résonnera avec votre âme.'
    }
  ];

  const stats = [
    { number: '200+', label: 'Villas d\'Exception', icon: Star },
    { number: '35', label: 'Destinations Exclusives', icon: Globe },
    { number: '98%', label: 'Satisfaction Client', icon: Award },
    { number: '10', label: 'Années d\'Excellence', icon: Clock }
  ];

  const team = [
    {
      name: 'Marie Dubois',
      role: 'Fondatrice & Directrice',
      specialty: 'Vision artistique & curation',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      description: 'Ancienne directrice artistique, Marie a créé PRIVIA pour démocratiser l\'accès aux plus belles demeures du monde.'
    },
    {
      name: 'Philippe Laurent',
      role: 'Directeur des Expériences',
      specialty: 'Concierge de luxe & relations',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      description: '20 ans dans l\'hôtellerie de prestige, Philippe orchestre chaque détail pour créer des moments magiques.'
    },
    {
      name: 'Sofia Andersson',
      role: 'Head of Curation',
      specialty: 'Sélection & architecture',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      description: 'Architecte de formation, Sofia parcourt le monde pour dénicher les perles rares de notre collection.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-8 px-4 py-2 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Heart className="w-4 h-4 mr-2" />
            Notre Histoire
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-8 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            L'Art de Vivre Réinventé
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-8 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Depuis 2014, PRIVIA révolutionne l'expérience du voyage de luxe en créant des ponts émotionnels entre nos clients et les plus belles demeures du monde
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge 
                variant="outline" 
                className="mb-6 px-4 py-2 text-amber-600 border-amber-200 bg-amber-50"
              >
                Notre Mission
              </Badge>
              
              <h2 
                className="text-4xl font-light mb-6 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Créer des Émotions Authentiques
              </h2>
              
              <p 
                className="text-lg text-gray-700 leading-relaxed mb-6"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Chez PRIVIA, nous croyons que chaque voyage doit être une renaissance. Notre mission transcende la simple location de villas : nous créons des expériences qui marquent une vie, des souvenirs qui deviennent des trésors.
              </p>
              
              <p 
                className="text-lg text-gray-700 leading-relaxed mb-8"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Notre approche unique par les émotions nous permet de vous guider vers la villa qui résonnera parfaitement avec vos aspirations les plus profondes, créant une harmonie parfaite entre le lieu et votre état d'âme.
              </p>
              
              <Button 
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105"
              >
                Découvrir notre Collection
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Villa PRIVIA"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="text-2xl font-light text-amber-600 mb-2">"L'excellence"</p>
                <p className="text-sm text-gray-600">
                  Notre signature dans chaque détail de votre expérience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Nos Valeurs Fondatrices
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Quatre piliers guident chacune de nos actions et façonnent l'expérience PRIVIA
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card 
                  key={index}
                  className="p-8 hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm transform hover:-translate-y-1"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                      <IconComponent className="w-8 h-8" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 
                        className="text-xl font-semibold mb-4 text-gray-800"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {value.title}
                      </h3>
                      <p 
                        className="text-gray-600 leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {value.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              PRIVIA en Chiffres
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Une décennie d'excellence au service de vos rêves d'évasion
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-light mb-2">{stat.number}</div>
                  <p className="text-amber-100 text-sm uppercase tracking-wider">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              L'Équipe PRIVIA
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Des passionnés au service de vos émotions les plus précieuses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card 
                key={index}
                className="p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="text-center">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-6 border-4 border-amber-100"
                  />
                  <h3 
                    className="text-xl font-semibold mb-2 text-gray-800"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {member.name}
                  </h3>
                  <p className="text-amber-600 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-gray-500 mb-4">{member.specialty}</p>
                  <p 
                    className="text-gray-600 text-sm leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {member.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Prêt à Vivre l'Expérience PRIVIA ?
          </h2>
          <p 
            className="text-xl text-gray-600 mb-8 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nos conseillers sont à votre écoute pour créer votre voyage sur-mesure
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              Planifier un Appel
            </Button>
            
            <Button 
              variant="outline"
              size="lg" 
              className="border-2 border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              <Mail className="w-5 h-5 mr-2" />
              Nous Contacter
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;